import { Fragment, type ReactNode } from 'react';

import type { Block } from '../../shared/content-types.js';
import { useHref } from '../i18n.js';

/**
 * Renders the tiny inline markup used by translated content.
 *
 * Supports exactly two forms, `**bold**` and `[label](/path)`, and produces React
 * elements directly. There is no HTML parsing and no `dangerouslySetInnerHTML`, so a
 * translation file cannot inject markup into a page however it is written.
 *
 * Internal links are written once, in English route form, and rewritten into the active
 * language here. That way a translator never has to remember a URL prefix, and a French
 * page can never link a reader back into the English site by accident.
 *
 * External links get `rel="noopener noreferrer nofollow"` automatically; internal ones do
 * not, so the language switcher and site navigation still pass link equity.
 */

const TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

export function RichText({ text }: { text: string }): ReactNode {
  const href = useHref();
  const parts = text.split(TOKEN).filter((part) => part !== '');

  return parts.map((part, index) => {
    const key = `${String(index)}-${part.slice(0, 12)}`;

    const bold = /^\*\*([^*]+)\*\*$/.exec(part);
    if (bold) return <strong key={key}>{bold[1]}</strong>;

    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part);
    if (link) {
      const target = link[2] ?? '';
      const internal = target.startsWith('/');
      return (
        <a
          key={key}
          href={internal ? href(target) : target}
          {...(internal ? {} : { rel: 'noopener noreferrer nofollow' })}
        >
          {link[1]}
        </a>
      );
    }

    return <Fragment key={key}>{part}</Fragment>;
  });
}

/** Render a sequence of content blocks as prose. */
export function Blocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <>
      {blocks.map((block, index) => {
        const key = String(index);
        if ('h2' in block) return <h2 key={key}>{block.h2}</h2>;
        if ('h3' in block) return <h3 key={key}>{block.h3}</h3>;
        if ('p' in block)
          return (
            <p key={key}>
              <RichText text={block.p} />
            </p>
          );
        if ('ul' in block)
          return (
            <ul key={key}>
              {block.ul.map((item, i) => (
                <li key={String(i)}>
                  <RichText text={item} />
                </li>
              ))}
            </ul>
          );
        return (
          <ol key={key}>
            {block.ol.map((item, i) => (
              <li key={String(i)}>
                <RichText text={item} />
              </li>
            ))}
          </ol>
        );
      })}
    </>
  );
}
